import React, { useEffect, useRef, useState } from 'react';
import { SYNC_AUTH_EVENT, isApiKeyFromEnv } from '../../services/syncApi';

/**
 * API key + user identity control, shown in the top bar.
 * Replaces the credentials card that used to live in the Sync Ops sidebar.
 */
const ApiKeyControl = () => {
  const [apiKey, setApiKey] = useState(localStorage.getItem('syncApiKey') || '');
  const [user, setUser] = useState(localStorage.getItem('syncUser') || '');
  const [authFailed, setAuthFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onAuthError = () => {
      setAuthFailed(true);
      setOpen(true);
      window.setTimeout(() => setAuthFailed(false), 2400);
    };
    window.addEventListener(SYNC_AUTH_EVENT, onAuthError);
    return () => window.removeEventListener(SYNC_AUTH_EVENT, onAuthError);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onClickAway = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const keyFromEnv = !apiKey && isApiKeyFromEnv();
  const hasKey = Boolean(apiKey) || keyFromEnv;
  const hasUser = Boolean(user);

  const saveCreds = () => {
    localStorage.setItem('syncApiKey', apiKey);
    localStorage.setItem('syncUser', user);
    window.location.reload();
  };

  const status = authFailed ? 'error' : hasKey ? 'ok' : 'warn';
  const label = authFailed ? 'Auth failed' : hasKey ? 'Key active' : 'Key required';
  const note = !authFailed && hasKey && keyFromEnv ? '· from .env' : null;

  return (
    <div className={`apikey-control apikey-${status}`} ref={rootRef}>
      <button
        type="button"
        className={`apikey-toggle${authFailed ? ' apikey-pulse' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="apikey-dot" aria-hidden="true" />
        <span className="apikey-label">{label}</span>
        {note && <span className="apikey-note">{note}</span>}
        <span className="apikey-chevron" aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="apikey-popover" role="dialog" aria-label="API credentials">
          {authFailed && (
            <p className="apikey-message apikey-message-error">
              Authentication failed — check your API key below.
            </p>
          )}
          {!hasKey && (
            <p className="apikey-message apikey-message-warn">
              Write actions (sync, overrides) need an API key. Read-only views still work.
            </p>
          )}
          {keyFromEnv && (
            <p className="apikey-message apikey-message-ok">
              Using key from <code>frontend/.env</code>. Paste a key below only to override it.
            </p>
          )}

          <label className="apikey-field">
            API Key
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={keyFromEnv ? 'Leave blank to use .env key' : 'Paste your API key'}
              autoComplete="off"
            />
          </label>

          <label className="apikey-field">
            Your name / email
            <input
              type="text"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="e.g. alice@waterco.com"
              autoComplete="off"
            />
          </label>
          {hasKey && !hasUser && (
            <p className="apikey-message apikey-message-warn">
              Add your name so changes are attributed in the audit log.
            </p>
          )}

          <button type="button" onClick={saveCreds} className="apikey-save">
            Save &amp; reload
          </button>
        </div>
      )}
    </div>
  );
};

export default ApiKeyControl;
